/**
 * EntitiesDetailedTab: monthly-grouped expandable transaction table for all
 * entities. Follows the CountryBookingsContent pattern from
 * PropertyPortfolioPage (month header rows, chevron-toggle expansion,
 * line-level detail, filter chips).
 *
 * Data source: GET /transactions (transactionsAPI.getAll).
 *
 * Uses function Foo(props) with body destructuring per 147f605 guard.
 */

import { useState, useEffect, useMemo } from 'react';
import { Loader2, ChevronDown, ChevronUp, RefreshCw, Filter } from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../ui/table';
import { transactionsAPI } from '../../../lib/api';
import { useEntityStore } from '../../../store';
import { toast } from 'sonner';
import { getErrorMessage } from '../../../lib/errors';
import { transactionCategoryLabel as categoryLabel } from '../../../lib/transactionCategory';

var MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

var MONTH_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function fmt(v, currency) {
  if (v == null) return '\u2014';
  var n = parseFloat(v);
  if (isNaN(n)) return '\u2014';
  // Only GBP and EUR get a symbol; every other currency shows its code, so
  // a DKK or USD total is never printed with a pound sign.
  var cur = String(currency || 'GBP').toUpperCase();
  var sym = cur === 'EUR' ? '\u20ac' : cur === 'GBP' ? '\u00a3' : cur + ' ';
  var abs = Math.abs(n);
  var formatted = sym + abs.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? '-' + formatted : formatted;
}

// Render a server-computed per-currency totals array into one cell string,
// e.g. "£1,200.00 · €300.00". Currencies are shown separately, never summed.
function fmtByCurrency(byCurrency, field) {
  if (!byCurrency || byCurrency.length === 0) return '—';
  var parts = [];
  for (var i = 0; i < byCurrency.length; i++) {
    var c = byCurrency[i];
    var val = parseFloat(c[field]) || 0;
    if (val !== 0) parts.push(fmt(val, c.currency));
  }
  return parts.length > 0 ? parts.join(' · ') : '—';
}

// A month/grand total is computed by summing tx.amount with no currency
// check (see monthlyGroups/grandTotals below). When the underlying
// transactions span more than one currency this sum is not meaningful and
// the hardcoded 'GBP' label on it is wrong; this badge flags that case
// without changing the sum itself.
function MixedCurrencyBadge() {
  return (
    <Badge
      variant="outline"
      data-testid="mixed-currency-badge"
      className="ml-2 text-[10px] px-1.5 py-0 h-4 border-amber-400 text-amber-700 dark:border-amber-600 dark:text-amber-400"
      title="Mixed currencies: this total sums amounts across more than one currency without conversion, the figure shown is not a true GBP total."
    >
      Mixed currencies
    </Badge>
  );
}

function colorClass(v) {
  var n = parseFloat(v);
  if (isNaN(n) || n === 0) return '';
  return n > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
}

function formatDate(d) {
  if (!d) return '\u2014';
  var dt = new Date(d);
  if (isNaN(dt.getTime())) return '\u2014';
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function EntitiesDetailedTab() {
  var txState = useState([]);
  var transactions = txState[0];
  var setTransactions = txState[1];
  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  // Server-computed grand totals over the FULL filtered set + the full count,
  // so the Total row never sums the capped rows.
  var totalsState = useState(null);
  var serverTotals = totalsState[0];
  var setServerTotals = totalsState[1];
  var fullCountState = useState(0);
  var fullCount = fullCountState[0];
  var setFullCount = fullCountState[1];
  var expandedState = useState(new Set());
  var expandedMonths = expandedState[0];
  var setExpandedMonths = expandedState[1];
  var entityFilterState = useState([]);
  var selectedEntities = entityFilterState[0];
  var setSelectedEntities = entityFilterState[1];
  var categoryFilterState = useState([]);
  var selectedCategories = categoryFilterState[0];
  var setSelectedCategories = categoryFilterState[1];

  var entities = useEntityStore(function (s) { return s.entities; });
  var entityById = useMemo(function () {
    var map = {};
    for (var i = 0; i < entities.length; i++) {
      map[entities[i].id] = entities[i];
      map[entities[i].type] = entities[i];
    }
    return map;
  }, [entities]);

  var fetchData = function () {
    setLoading(true);
    transactionsAPI.getAll({ page_size: 200 })
      .then(function (res) {
        var data = res.data || {};
        var list = Array.isArray(data) ? data : (data.transactions || []);
        setTransactions(list);
        setServerTotals(data.totals || null);
        setFullCount(typeof data.total === 'number' ? data.total : list.length);
      })
      .catch(function (err) { toast.error(getErrorMessage(err)); })
      .finally(function () { setLoading(false); });
  };

  useEffect(function () { fetchData(); }, []);

  // Filter
  var filtered = useMemo(function () {
    var list = transactions;
    if (selectedEntities.length > 0) {
      list = list.filter(function (tx) {
        return selectedEntities.indexOf(tx.entity_id) !== -1;
      });
    }
    if (selectedCategories.length > 0) {
      list = list.filter(function (tx) {
        return selectedCategories.indexOf(tx.category || 'other') !== -1;
      });
    }
    return list;
  }, [transactions, selectedEntities, selectedCategories]);

  // Group by month
  var monthlyGroups = useMemo(function () {
    var groups = {};
    for (var i = 0; i < filtered.length; i++) {
      var tx = filtered[i];
      var dt = new Date(tx.date);
      if (isNaN(dt.getTime())) continue;
      var monthKey = dt.getFullYear() + '-' + String(dt.getMonth() + 1).padStart(2, '0');
      if (!groups[monthKey]) {
        groups[monthKey] = {
          key: monthKey,
          month: dt.getMonth() + 1,
          year: dt.getFullYear(),
          label: MONTH_LABELS[dt.getMonth()] + ' ' + dt.getFullYear(),
          items: [],
          totalIn: 0,
          totalOut: 0,
          count: 0,
          currencies: {},
        };
      }
      var g = groups[monthKey];
      g.items.push(tx);
      var amt = parseFloat(tx.amount) || 0;
      if (amt > 0) { g.totalIn += amt; }
      else { g.totalOut += amt; }
      g.count += 1;
      g.currencies[tx.currency || 'GBP'] = true;
    }

    // Sort items within each group by date desc
    var keys = Object.keys(groups).sort().reverse();
    return keys.map(function (k) {
      var g = groups[k];
      g.items.sort(function (a, b) {
        return new Date(b.date) - new Date(a.date);
      });
      // totalIn/totalOut/net above sum tx.amount with no currency
      // conversion; flag when that sum silently mixed currencies together.
      g.mixedCurrency = Object.keys(g.currencies).length > 1;
      return g;
    });
  }, [filtered]);

  // Unique filter options
  var entityOptions = useMemo(function () {
    var seen = {};
    for (var i = 0; i < transactions.length; i++) {
      var eid = transactions[i].entity_id;
      if (eid && !seen[eid]) {
        var ent = entityById[eid];
        seen[eid] = ent ? ent.name : eid;
      }
    }
    return Object.entries(seen).sort(function (a, b) { return a[1].localeCompare(b[1]); });
  }, [transactions, entityById]);

  var categoryOptions = useMemo(function () {
    var cats = {};
    for (var i = 0; i < transactions.length; i++) {
      var c = transactions[i].category || 'other';
      cats[c] = true;
    }
    return Object.keys(cats).sort();
  }, [transactions]);

  var toggleFilter = function (arr, setArr, val) {
    if (arr.indexOf(val) !== -1) {
      setArr(arr.filter(function (v) { return v !== val; }));
    } else {
      setArr(arr.concat([val]));
    }
  };

  var toggleMonth = function (key) {
    setExpandedMonths(function (prev) {
      var next = new Set(prev);
      if (next.has(key)) { next.delete(key); } else { next.add(key); }
      return next;
    });
  };

  // Grand totals
  var grandTotals = useMemo(function () {
    var totalIn = 0;
    var totalOut = 0;
    var count = 0;
    var currencies = {};
    for (var i = 0; i < monthlyGroups.length; i++) {
      totalIn += monthlyGroups[i].totalIn;
      totalOut += monthlyGroups[i].totalOut;
      count += monthlyGroups[i].count;
      var groupCurrencies = Object.keys(monthlyGroups[i].currencies);
      for (var j = 0; j < groupCurrencies.length; j++) {
        currencies[groupCurrencies[j]] = true;
      }
    }
    return {
      totalIn: totalIn,
      totalOut: totalOut,
      net: totalIn + totalOut,
      count: count,
      mixedCurrency: Object.keys(currencies).length > 1,
    };
  }, [monthlyGroups]);

  if (loading && transactions.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Detailed Transactions</h2>
          <p className="text-sm text-muted-foreground">
            All bank transactions across entities, grouped by month.
          </p>
          {fullCount > transactions.length && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
              Showing the {transactions.length} most recent of {fullCount}. The Total row below covers all {fullCount}.
            </p>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
          <RefreshCw className={'h-4 w-4 mr-2' + (loading ? ' animate-spin' : '')} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <Filter className="h-4 w-4" /> Filters:
            </div>

            {/* Entity filter */}
            <select
              className="text-sm border rounded px-2 py-1 bg-card"
              value=""
              onChange={function (e) { if (e.target.value) toggleFilter(selectedEntities, setSelectedEntities, e.target.value); }}
            >
              <option value="">Entity ({selectedEntities.length || 'All'})</option>
              {entityOptions.map(function (pair) {
                var eid = pair[0];
                var name = pair[1];
                return (
                  <option key={eid} value={eid}>
                    {selectedEntities.indexOf(eid) !== -1 ? '\u2713 ' : ''}{name}
                  </option>
                );
              })}
            </select>

            {/* Category filter */}
            <select
              className="text-sm border rounded px-2 py-1 bg-card"
              value=""
              onChange={function (e) { if (e.target.value) toggleFilter(selectedCategories, setSelectedCategories, e.target.value); }}
            >
              <option value="">Category ({selectedCategories.length || 'All'})</option>
              {categoryOptions.map(function (cat) {
                return (
                  <option key={cat} value={cat}>
                    {selectedCategories.indexOf(cat) !== -1 ? '\u2713 ' : ''}{categoryLabel(cat)}
                  </option>
                );
              })}
            </select>

            {/* Active filter badges */}
            {(selectedEntities.length > 0 || selectedCategories.length > 0) && (
              <>
                {selectedEntities.map(function (eid) {
                  var ent = entityById[eid];
                  return (
                    <Badge key={eid} variant="secondary" className="text-xs cursor-pointer" onClick={function () { toggleFilter(selectedEntities, setSelectedEntities, eid); }}>
                      {ent ? ent.name : eid} x
                    </Badge>
                  );
                })}
                {selectedCategories.map(function (cat) {
                  return (
                    <Badge key={cat} variant="secondary" className="text-xs cursor-pointer" onClick={function () { toggleFilter(selectedCategories, setSelectedCategories, cat); }}>
                      {categoryLabel(cat)} x
                    </Badge>
                  );
                })}
                <Button variant="ghost" size="sm" className="text-xs h-6" onClick={function () { setSelectedEntities([]); setSelectedCategories([]); }}>
                  Clear all
                </Button>
              </>
            )}

            <span className="text-xs text-muted-foreground ml-auto">{filtered.length} transactions</span>
          </div>
        </CardContent>
      </Card>

      {/* Monthly expandable table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Inflows</TableHead>
                  <TableHead className="text-right">Outflows</TableHead>
                  <TableHead className="text-right">Net</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monthlyGroups.map(function (g) {
                  var isExpanded = expandedMonths.has(g.key);
                  var net = g.totalIn + g.totalOut;
                  var now = new Date();
                  var isCurrent = g.year === now.getFullYear() && g.month === now.getMonth() + 1;

                  return (
                    <>
                      {/* Month header row */}
                      <TableRow
                        key={g.key}
                        className={
                          (isCurrent ? 'bg-primary/5 ' : '') +
                          (g.count > 0 ? 'cursor-pointer hover:bg-muted/50' : '')
                        }
                        onClick={g.count > 0 ? function () { toggleMonth(g.key); } : undefined}
                      >
                        <TableCell className="w-8 px-2">
                          {g.count > 0 && (isExpanded
                            ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                            : <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          )}
                        </TableCell>
                        <TableCell className="font-medium">
                          {g.label}
                          {isCurrent && (
                            <Badge className="ml-2 text-[10px] px-1.5 py-0 h-4 bg-primary/10 text-primary border-0">
                              Now
                            </Badge>
                          )}
                          <span className="ml-2 text-xs text-muted-foreground">
                            {g.count} transaction{g.count !== 1 ? 's' : ''}
                          </span>
                          {g.mixedCurrency && <MixedCurrencyBadge />}
                        </TableCell>
                        <TableCell className="text-right font-mono text-green-600 dark:text-green-400">
                          {g.totalIn > 0 ? fmt(g.totalIn, 'GBP') : '\u2014'}
                        </TableCell>
                        <TableCell className="text-right font-mono text-red-600 dark:text-red-400">
                          {g.totalOut < 0 ? fmt(g.totalOut, 'GBP') : '\u2014'}
                        </TableCell>
                        <TableCell className={'text-right font-mono font-semibold ' + colorClass(net)}>
                          {fmt(net, 'GBP')}
                        </TableCell>
                      </TableRow>

                      {/* Expanded transaction detail rows */}
                      {isExpanded && g.items.map(function (tx, idx) {
                        var ent = entityById[tx.entity_id];
                        return (
                          <TableRow key={g.key + '-' + idx} className="bg-muted/20 text-xs">
                            <TableCell></TableCell>
                            <TableCell className="py-1.5" colSpan={1}>
                              <span className="font-medium">{formatDate(tx.date)}</span>
                              <span className="text-muted-foreground ml-2">{tx.counterparty || tx.description || '\u2014'}</span>
                              {ent && (
                                <Badge variant="outline" className="ml-2 text-[10px] px-1 py-0">{ent.name}</Badge>
                              )}
                              <Badge variant="outline" className="ml-1 text-[10px] px-1 py-0">{categoryLabel(tx.category)}</Badge>
                              {tx.is_reconciled && (
                                <Badge className="ml-1 text-[10px] px-1 py-0 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border-0">
                                  Reconciled
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-mono py-1.5 text-green-600 dark:text-green-400">
                              {parseFloat(tx.amount) > 0 ? fmt(tx.amount, tx.currency || 'GBP') : ''}
                            </TableCell>
                            <TableCell className="text-right font-mono py-1.5 text-red-600 dark:text-red-400">
                              {parseFloat(tx.amount) < 0 ? fmt(tx.amount, tx.currency || 'GBP') : ''}
                            </TableCell>
                            <TableCell className={'text-right font-mono py-1.5 ' + colorClass(tx.amount)}>
                              {fmt(tx.amount, tx.currency || 'GBP')}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </>
                  );
                })}

                {/* Grand total row: server-computed over the FULL filtered set
                    (all N transactions), never a client sum of the capped rows.
                    Falls back to the client sum only if the server omits totals. */}
                {serverTotals ? (
                  <TableRow className="border-t-2 font-semibold bg-muted/30">
                    <TableCell></TableCell>
                    <TableCell>Total (all {serverTotals.count} transaction{serverTotals.count !== 1 ? 's' : ''})</TableCell>
                    <TableCell className="text-right font-mono text-green-600 dark:text-green-400">
                      {fmtByCurrency(serverTotals.by_currency, 'inflow')}
                    </TableCell>
                    <TableCell className="text-right font-mono text-red-600 dark:text-red-400">
                      {fmtByCurrency(serverTotals.by_currency, 'outflow')}
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold">
                      {fmtByCurrency(serverTotals.by_currency, 'net')}
                    </TableCell>
                  </TableRow>
                ) : (monthlyGroups.length > 0 && (
                  <TableRow className="border-t-2 font-semibold bg-muted/30">
                    <TableCell></TableCell>
                    <TableCell>
                      Total ({grandTotals.count} transactions)
                      {grandTotals.mixedCurrency && <MixedCurrencyBadge />}
                    </TableCell>
                    <TableCell className="text-right font-mono text-green-600 dark:text-green-400">
                      {grandTotals.totalIn > 0 ? fmt(grandTotals.totalIn, 'GBP') : '\u2014'}
                    </TableCell>
                    <TableCell className="text-right font-mono text-red-600 dark:text-red-400">
                      {grandTotals.totalOut < 0 ? fmt(grandTotals.totalOut, 'GBP') : '\u2014'}
                    </TableCell>
                    <TableCell className={'text-right font-mono font-semibold ' + colorClass(grandTotals.net)}>
                      {fmt(grandTotals.net, 'GBP')}
                    </TableCell>
                  </TableRow>
                ))}

                {monthlyGroups.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                      No transactions found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
